import { FormikErrors } from "formik";
import { capitalize, cloneDeep } from "lodash";
import moment from "moment";
import Papa from "papaparse";
import { numericFormatter } from "react-number-format";

import GlobalSettingsApi from "../api/GlobalSettingsApi";
import { appConstants, FileSizes } from "../config/app-constants";
import { CountryWithCode, RegimeTypes, StateWithCode } from "../config/filingConstants";
import { IFormITR1, PincodeData } from "../config/interfaces";

export default class UtilService {
  static dispatchObj: any = null;
  static handleLogoutOnUnauthorized: any = null;

  static getErrorMessage = (e: any): string => {
    return e?.response?.data?.message
      ? e.response.data.message
      : e.message || "An error occurred, please try again later.";
  };

  static convertToArray(obj: { [key: string]: string }, labelProp = "label", valueProp = "value") {
    const arr: Array<any> = [];
    Object.keys(obj).forEach((key: string) => {
      arr.push({
        [labelProp]: obj[key],
        [valueProp]: key
      });
    });
    return arr;
  }

  // it show country with country code
  static convertToCountryCode(
    obj: { [key: string]: string },
    labelProp = "label",
    valueProp = "value"
  ) {
    const arr: Array<any> = [];
    Object.keys(obj).forEach((key: string) => {
      arr.push({
        [labelProp]: `+${key} (${obj[key]})`,
        [valueProp]: key
      });
    });
    return arr;
  }

  static validateSelectedFile = (selectedFile: Blob) => {
    if (!selectedFile) {
      // throw { message: "Please select a file" };
      throw new Error("Please select a file");
    }

    const fileSizeKiloBytes = selectedFile.size / 1024;

    if (fileSizeKiloBytes > FileSizes.MAX_FILE_SIZE) {
      // throw { message: "Selected file size is greater than 2MB" };
      throw new Error("Selected file size is greater than 2MB");
    }
  };

  static validateSelectedFileAndPDF = (selectedFile: Blob) => {
    let _message = "Selected file size is greater than 1 MB";
    let MAX_FILE_SIZE = 1024;
    const fileType = selectedFile.type;

    if (fileType.includes("pdf")) {
      MAX_FILE_SIZE = 2048;
      _message = "Selected file size is greater than 2 MB";
    }
    const fileSizeKiloBytes = selectedFile.size / 1024;
    if (fileSizeKiloBytes > MAX_FILE_SIZE) {
      // throw { message: _message };
      throw new Error(_message);
    }
  };

  static setDispatch(dispatch: any) {
    this.dispatchObj = dispatch;
  }

  static setLogoutOnUnauthorized(callback: () => void) {
    this.handleLogoutOnUnauthorized = callback;
  }

  static isAuthenticated() {
    const loginData = localStorage.getItem("_api_token");
    // const isTermsAccepted = localStorage.getItem("_is_terms_accepted");
    // return loginData && isTermsAccepted ? true : false;

    return loginData ? true : false;
  }

  static getUSADateTimeDisplay = (dateString: string) => {
    const _date = moment(new Date(dateString).toISOString()).format(appConstants.longDateFormat);
    return _date;
  };

  // convert into DD MMM YYYY hh mm format
  static getDateTimeDisplay = (dateString: string) => {
    const _date = moment(new Date(dateString).toISOString()).format(appConstants.dateTimeFormate);
    return _date;
  };

  // convert into YYYY-MM-DD format
  static convertToStandardDateFormat = (dateString: string | Date) => {
    const _date = moment(dateString).format(appConstants.standardDateFormat);
    return _date;
  };

  static validateDecimalNumber = (value: number | string) => {
    // if (value.toString().endsWith(".")) return false; // 12. is invalid
    if (/^\d+(\.\d{0,2})?$/.test(String(value))) return true;
    return false;
  };

  static formatNumber = (props: { number: string | number; decimalUpto?: number }) => {
    const { number, decimalUpto = 2 } = props;
    return numericFormatter(String(number), {
      decimalScale: decimalUpto,
      thousandSeparator: true,
      fixedDecimalScale: true
    });
  };

  static convertToPrice = (props: { price: string | number; decimalUpto?: number }) => {
    const { price, decimalUpto = 2 } = props;
    return numericFormatter(String(price), {
      decimalScale: decimalUpto,
      thousandSeparator: true,
      prefix: "₹ ",
      fixedDecimalScale: true,
      thousandsGroupStyle: "lakh"
    });
  };

  static Capitalize = (text: string) => {
    return capitalize(text);
  };

  static updateObject(ref: any, keys: any, value: any) {
    const key = keys.pop();
    if (keys.length) {
      ref[key] = this.updateObject(ref[key], keys, value);
    } else {
      ref[key] = value;
    }
    return ref;
  }

  static updateSchemaData(formikData: any, schemaData: any, mappingData: any) {
    Object.keys(mappingData).forEach((key) => {
      const schemaKey = mappingData[key];
      let ref = formikData;
      key.split(".").forEach((part) => {
        ref = ref[part];
      });
      schemaData = this.updateObject(schemaData, schemaKey.split(".").reverse(), ref);
    });
    return schemaData;
  }

  static setSchemaDataToForm(formikData: any, schemaData: any, mappingData: any) {
    Object.keys(mappingData).forEach((value: any) => {
      const schemaKey = mappingData[value];
      let ref = formikData;
      value.split(".").forEach((part: any) => {
        ref = ref[part];
      });
      schemaData = this.updateObject(schemaData, schemaKey.split(".").reverse(), ref);
    });
    return schemaData;
  }

  // to convert object { "name": "ravindra" } into { "ravindra": "name" }
  // object key and value will be interchanged to each other
  static switchObjKeyToValue = (originalObject: { [key: string]: string }) => {
    let newObj: { [key: string]: string } = {};
    Object.entries(originalObject).forEach(([key, value]) => {
      newObj[value] = key;
    });
    return newObj;
  };

  static getStateCityFromPincode = async (pincode: string): Promise<PincodeData | null> => {
    let responseData: PincodeData | null = null;
    try {
      const api = GlobalSettingsApi.getInstance();
      const [data] = await api.getStateByPinCode(pincode);

      if (data.PostOffice) {
        const city = data.PostOffice[0].District;
        const state = data.PostOffice[0].State;
        const country = data.PostOffice[0].Country.toUpperCase();
        const _StateWithCode = UtilService.switchObjKeyToValue(StateWithCode);
        const _CountryWithCode = UtilService.switchObjKeyToValue(CountryWithCode);

        responseData = {
          CountryCode: _CountryWithCode[country] || "",
          StateCode: _StateWithCode[state] || "",
          CityOrTownOrDistrict: city
        };
      }
      return responseData;
    } catch (error) {
      console.log("Get data by pincode error ", UtilService.getErrorMessage(error));
      this.dispatchObj();
      return responseData;
    }
  };

  static getTimeDisplay = (dateString: string) => {
    const _time = moment(new Date(dateString).toISOString()).format(appConstants.timeFormat);
    return _time;
  };

  static getStateOptions = (CountryCode: string) => {
    if (CountryCode === "91") {
      const states = UtilService.convertToArray(StateWithCode).filter(
        (state) => state.value !== "99"
      );
      return states;
    } else {
      const states = UtilService.convertToArray(StateWithCode).filter(
        (state) => state.value === "99"
      );
      return states;
    }
  };

  static GetError = (
    [...fields]: string[],
    allValidationError: FormikErrors<IFormITR1>,
    errors: FormikErrors<IFormITR1>
  ) => {
    const _AllError = Object.keys(allValidationError || {});
    for (const field of fields) {
      if (
        _AllError.includes(field) &&
        Object.keys(errors).includes(field)
        // || Object.keys(errors).includes(field)
      ) {
        return true;
      }
    }
    return false;
  };

  static calculateOldRegimeTax = (
    taxableIncome: number,
    DOB: string
  ): {
    "TotalTaxPayable": number;
    "Rebate87A": number;
    "TaxPayableOnRebate": number;
    "EducationCess": number;
  } => {
    let tax = 0;
    const ageInYears = DOB ? this.getUserAge(new Date(DOB)) : 25;

    if (ageInYears < 60) {
      if (taxableIncome <= 250000) {
        tax = 0;
      } else if (taxableIncome <= 500000) {
        tax = (taxableIncome - 250000) * 0.05;
      } else if (taxableIncome <= 1000000) {
        tax = 250000 * 0.05 + (taxableIncome - 500000) * 0.2;
      } else {
        tax = 250000 * 0.05 + 500000 * 0.2 + (taxableIncome - 1000000) * 0.3;
      }
    } else if (ageInYears >= 60 && ageInYears <= 80) {
      if (taxableIncome <= 300000) {
        tax = 0;
      } else if (taxableIncome <= 500000) {
        tax = (taxableIncome - 300000) * 0.05;
      } else if (taxableIncome <= 1000000) {
        tax = 200000 * 0.05 + (taxableIncome - 500000) * 0.2;
      } else {
        tax = 200000 * 0.05 + 500000 * 0.2 + (taxableIncome - 1000000) * 0.3;
      }
    } else {
      if (taxableIncome <= 500000) {
        tax = 0;
      } else if (taxableIncome <= 1000000) {
        tax = (taxableIncome - 500000) * 0.2;
      } else {
        tax = 500000 * 0.2 + (taxableIncome - 1000000) * 0.3;
      }
    }

    const TotalTaxPayable = tax;
    let Rebate87A = 0;
    // Apply rebate under Section 87A if applicable
    if (taxableIncome <= 500000) {
      Rebate87A = TotalTaxPayable;
    }

    const _TaxPayableOnRebate = TotalTaxPayable - Rebate87A;

    // Add Health and Education Cess
    const EducationCess = _TaxPayableOnRebate * 0.04;

    const taxDetails = {
      "TotalTaxPayable": TotalTaxPayable,
      "Rebate87A": Rebate87A,
      "TaxPayableOnRebate": _TaxPayableOnRebate,
      "EducationCess": EducationCess
    };

    return taxDetails;
  };

  static calOldTaxRegimeSurcharge = (taxableIncome: number, tax: number) => {
    let Surcharge = 0;

    if (taxableIncome <= 5000000) {
      Surcharge = 0;
    } else if (taxableIncome <= 10000000) {
      Surcharge = tax * 0.1;
    } else if (taxableIncome <= 20000000) {
      Surcharge = tax * 0.15;
    } else if (taxableIncome <= 50000000) {
      Surcharge = tax * 0.25;
    } else {
      Surcharge = tax * 0.37;
    }

    return Surcharge;
  };

  static calculateNewRegimeTax = (
    taxableIncome: number
  ): {
    "TotalTaxPayable": number;
    "Rebate87A": number;
    "TaxPayableOnRebate": number;
    "EducationCess": number;
  } => {
    let tax = 0;

    if (taxableIncome <= 300000) {
      tax = 0;
    } else if (taxableIncome <= 600000) {
      tax = (taxableIncome - 300000) * 0.05;
    } else if (taxableIncome <= 900000) {
      tax = 300000 * 0.05 + (taxableIncome - 600000) * 0.1;
    } else if (taxableIncome <= 1200000) {
      tax = 300000 * 0.05 + 300000 * 0.1 + (taxableIncome - 900000) * 0.15;
    } else if (taxableIncome <= 1500000) {
      tax = 300000 * 0.05 + 300000 * 0.1 + 300000 * 0.15 + (taxableIncome - 1200000) * 0.2;
    } else {
      tax =
        300000 * 0.05 +
        300000 * 0.1 +
        300000 * 0.15 +
        300000 * 0.2 +
        (taxableIncome - 1500000) * 0.3;
    }

    const TotalTaxPayable = tax;
    let Rebate87A = 0;
    // Apply rebate under Section 87A if applicable
    if (taxableIncome <= 700000) {
			Rebate87A = TotalTaxPayable;
		}

    const _TaxPayableOnRebate = TotalTaxPayable - Rebate87A;

    // Add Health and Education Cess
    const EducationCess = _TaxPayableOnRebate * 0.04;

    const taxDetails = {
      "TotalTaxPayable": TotalTaxPayable,
      "Rebate87A": Rebate87A,
      "TaxPayableOnRebate": _TaxPayableOnRebate,
      "EducationCess": EducationCess
    };

    return taxDetails;
  };

  static calNewTaxRegimeSurcharge = (taxableIncome: number, tax: number) => {
    let Surcharge = 0;

    if (taxableIncome <= 5000000) {
      Surcharge = 0;
    } else if (taxableIncome <= 10000000) {
      Surcharge = tax * 0.1;
    } else if (taxableIncome <= 20000000) {
      Surcharge = tax * 0.15;
    } else if (taxableIncome <= 50000000) {
      Surcharge = tax * 0.25;
    } else {
      Surcharge = tax * 0.25;
    }

    return Surcharge;
  };

  static calculateInterest234A = (taxPayable: number, monthsDelayed: number) => {
    return taxPayable * 0.01 * monthsDelayed;
  };

  static calculateInterest234B = (
    taxPayable: number,
    advanceTax: number,
    monthsDelayedPayment: number
  ) => {
    const _90PerTaxPayable = taxPayable * 0.9;
    if (_90PerTaxPayable > advanceTax) {
      return (taxPayable - advanceTax) * 0.01 * monthsDelayedPayment;
    }

    return 0;
  };

  static calculateInterest234C = (
    shortfallJune: number,
    shortfallSep: number,
    shortfallDec: number,
    shortfallMar: number
  ) => {
    let interest = 0;
    if (shortfallJune > 0) {
      interest += shortfallJune * 0.01 * 3;
    }
    if (shortfallSep > 0) {
      interest += shortfallSep * 0.01 * 3;
    }
    if (shortfallDec > 0) {
      interest += shortfallDec * 0.01 * 3;
    }
    if (shortfallMar > 0) {
      interest += shortfallMar * 0.01 * 1;
    }
    return interest;
  };

  static calculateFee234F = (taxPayable: number, filingDate: Date) => {
    const dueDate = new Date("2024-07-31");
    if (filingDate <= dueDate) {
      return 0;
    } else if (filingDate <= new Date("2024-12-31")) {
      return taxPayable <= 500000 ? 1000 : 5000;
    } else {
      return taxPayable <= 500000 ? 1000 : 10000;
    }
  };

  // Function to create a date string in the format "YYYY-YY"
  static createFinancialYear = (type: "full" | "partial") => {
    // Create a Date object
    const startDate = new Date();

    // Get the full year from the date
    const startYear = startDate.getFullYear();

    // Calculate the next year
    const endYear = startYear - 1;

    // Format the date string as "YYYY-YY"
    const year = `${endYear}-${startYear.toString().slice(-2)}`;

    if (type === "partial") {
      return year;
    }

    const fullYear = `${year} (Apr ${endYear} to Mar ${startYear})`;

    return fullYear;
  };

  // calculate date difference
  static calculateDateDifference = (PurchaseDate: Date | string, SaleDate: Date | string) => {
    const firstDate = new Date(PurchaseDate);
    const secondDate = new Date(SaleDate);

    // Calculate the difference in milliseconds
    const diffInMilliseconds = Math.abs(Number(secondDate) - Number(firstDate));

    // Convert milliseconds to days
    const diffInDays = Math.ceil(diffInMilliseconds / (1000 * 60 * 60 * 24));

    return diffInDays;
  };

  // Function to categorize age based on birthdate
  static getUserAge = (birthdate: Date) => {
    // Parse the birthdate string into a Date object
    const birthDate: any = new Date(birthdate);

    // Get the current date
    const currentDate: any = new Date();

    // Calculate the difference in milliseconds between the current date and the birthdate
    let ageInMillis = currentDate - birthDate;

    // Convert milliseconds to years
    let ageInYears = ageInMillis / (1000 * 60 * 60 * 24 * 365.25);

    return ageInYears;
  };

  // get Months From April (including april)
  static getMonthsFromApril = () => {
    let today = new Date();
    let currentMonth = today.getMonth() + 1; // getMonth() returns months from 0-11, so we add 1
    let april = 4; // April is the 4th month

    let monthsFromApril;

    if (currentMonth >= april) {
      monthsFromApril = currentMonth - april + 1;
    } else {
      monthsFromApril = 12 - (april - currentMonth) + 1;
    }

    return monthsFromApril;
  };

  // handle tax computation and taxes paid computation
  static getTaxAndTaxesPaidComputation = (
    values: IFormITR1,
    TaxDetails: any,
    RegimeType: string,
    _TotalTaxableIncome: number,
    _TotalSplRateTax: number
  ) => {
    const _TDS = values.TotalTDSonSalaries + values.TotalTDSonOthThanSals + values.TotalTDS3Details;
    const _TCS = values.TotalSchTCS;
    const _AdvanceTax = values.TotalSelfTaxPayments;
    const _SelfAssessmentTax = 0;
    const _TotalTaxesPaid = _AdvanceTax + _TDS + _TCS + _SelfAssessmentTax;
    const _TotalTaxPayable = TaxDetails.TaxPayableOnRebate + _TotalSplRateTax;
    let _Surcharge = 0;

    if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
      _Surcharge = UtilService.calNewTaxRegimeSurcharge(_TotalTaxableIncome, _TotalTaxPayable);
    } else {
      _Surcharge = UtilService.calOldTaxRegimeSurcharge(_TotalTaxableIncome, _TotalTaxPayable);
    }

    const _EducationCess = (_TotalTaxPayable + _Surcharge) * 0.04;
    let _Section89 = 0;
    const _GrossTaxLiability = _TotalTaxPayable + _Surcharge + _EducationCess;

    let monthsDelayed = 0; // Example delay for filing
    let monthsDelayedPayment = UtilService.getMonthsFromApril(); // Months delay for payment of tax

    // TODO: Not consider for calculation (confirmed from client)
    // let IntrstPayUs234A = UtilService.calculateInterest234A(_TotalTaxPayable, monthsDelayed);
    let IntrstPayUs234A = 0;
    let IntrstPayUs234B = UtilService.calculateInterest234B(
      _GrossTaxLiability - (_TDS + _TCS),
      _AdvanceTax,
      monthsDelayedPayment
    );
    // const TaxPayableFor234C = _GrossTaxLiability - _TotalSplRateTax - (_TDS + _TCS);
    const TaxPayableFor234C = _GrossTaxLiability - (_TDS + _TCS);
    const shortfallJune = 0.15 * TaxPayableFor234C - values.TaxPaidBefore15June;
    const shortfallSep = 0.45 * TaxPayableFor234C - values.TaxPaidBefore15Sept;
    const shortfallDec = 0.75 * TaxPayableFor234C - values.TaxPaidBefore15Dec;
    const shortfallMar = 1.0 * TaxPayableFor234C - values.TaxPaidBefore15Mar;
    let IntrstPayUs234C = UtilService.calculateInterest234C(
      shortfallJune,
      shortfallSep,
      shortfallDec,
      shortfallMar
    );
    // TODO: Not consider for calculation (confirmed from client)
    // let filingDate = new Date();
    // let LateFilingFee234F = UtilService.calculateFee234F(_TotalTaxPayable, filingDate);
    let LateFilingFee234F = 0;
    const _TotalIntrstPay = IntrstPayUs234A + IntrstPayUs234B + IntrstPayUs234C + LateFilingFee234F;
    const _NetTaxLiability = _GrossTaxLiability - _Section89;
    const _TotTaxPlusIntrstPay = _NetTaxLiability + _TotalIntrstPay;

    const TaxComputation = {
      IntrstPay: {
        IntrstPayUs234A: Math.round(IntrstPayUs234A),
        IntrstPayUs234B: Math.round(IntrstPayUs234B),
        IntrstPayUs234C: Math.round(IntrstPayUs234C),
        LateFilingFee234F: Math.round(LateFilingFee234F)
      },
      TotalTaxPayable: Math.round(TaxDetails.TotalTaxPayable),
      Rebate87A: Math.round(TaxDetails.Rebate87A),
      TotalSplRateTax: Math.round(_TotalSplRateTax),
      TaxPayableOnRebate: Math.round(TaxDetails.TaxPayableOnRebate),
      EducationCess: Math.round(_EducationCess),
      GrossTaxLiability: Math.round(_GrossTaxLiability),
      Surcharge: _Surcharge,
      Section89: _Section89,
      NetTaxLiability: Math.round(_NetTaxLiability),
      TotalIntrstPay: Math.round(_TotalIntrstPay),
      TotTaxPlusIntrstPay: Math.round(_TotTaxPlusIntrstPay)
    };

    const _BalTaxPayable = _TotTaxPlusIntrstPay - _TotalTaxesPaid;
    const TaxPaidComputation = {
      TaxesPaid: {
        AdvanceTax: Math.round(_AdvanceTax), // sum TotalSelfTaxPayments (TotalTaxPayments)
        TDS: Math.round(_TDS), // sum of TotalTDSonSalaries, TotalTDSonOthThanSals, TotalTDS3Details
        TCS: Math.round(_TCS), // sum of TotalSchTCS
        SelfAssessmentTax: Math.round(_SelfAssessmentTax),
        TotalTaxesPaid: Math.round(_TotalTaxesPaid) // sum of above values
      },
      BalTaxPayable: Math.round(_BalTaxPayable) // difference of TotTaxPlusIntrstPay and _TotalTaxesPaid
    };

    const DATA = {
      TaxComputation: cloneDeep(TaxComputation),
      TaxPaidComputation: cloneDeep(TaxPaidComputation)
    };

    return DATA;
  };

  // Used to generate year ranges
  static generateYearRanges = ({ start = 2018, end = new Date().getFullYear() } = {}) => {
    if (start > end) {
      return [];
    }

    let yearRanges = [];

    for (let year = start; year < end; year++) {
      yearRanges.push(`${year}-${year + 1}`);
    }
    return yearRanges.reverse();
  };

  // Used to scrollToTop
  static scrollToTop = () => {
    return document.querySelector(".main-container")?.scrollTo(0, 0);
  };

  static downloadCSV = (
    fileName: string,
    flattenedData: {
      fields: string[];
      data: (string | number)[][];
    }
  ) => {
    // Define PapaParse options
    const options = {
      quotes: false,
      delimiter: ",",
      header: true,
      newline: "\r\n",
      skipEmptyLines: false
    };

    // Convert JSON to CSV with options
    const csv = Papa.unparse(flattenedData, options);

    // Create a Blob from the CSV string
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    let hidden_a = document.createElement("a");
    hidden_a.href = url;
    hidden_a.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(hidden_a);
    hidden_a.click();
    document.body.removeChild(hidden_a);

    return url;
  };
}
